import { jwtDecode } from 'jwt-decode';
import {ApiError, getApiUrl, handleSessionExpired, makeApiCall, makePublicApiCall} from '../core';
import {getIdToken} from '../auth';
import {UpdateOrganizationRequest, ChargingMode, type OrganizationFirstRunState, type ChargeWaitMinutes} from '@ruta/shared';
// The wire shape this endpoint actually accepts (`organizationName`, not `name`).
// @ruta/shared exports two different CreateOrganizationRequest types, neither of
// which matches it — importing from there silently resolved to whichever won.
import type {CreateOrganizationRequest} from '../types';
import type {ItemEnvelope, ListEnvelope, PlatformInvoice, Role, PlatformProcessorActivity, PlatformUsageSummary, SetupChecklistItemKey, SetupChecklistStatus} from '@ruta/shared';

// Legacy types still used in webapp (to be migrated)
import type {
    InviteMemberRequest,
    OrganizationMember,
    OrganizationMembersResponse,
    UpdateMemberRequest,
} from '../types';

/** True when the staff JWT names a company — false mid-signup, before one exists. */
function tokenHasOrgId(): boolean {
  try {
    const token = getIdToken();
    if (!token) return false;
    return !!jwtDecode<Record<string, string>>(token)['custom:orgId'];
  } catch {
    return false;
  }
}

/** Org id from the staff JWT — required for org-scoped member routes. */
function requireOrgId(): string {
  const token = getIdToken();
  if (!token) throw new Error('Not authenticated');
  const claims = jwtDecode<Record<string, string>>(token);
  const orgId = claims['custom:orgId'];
  if (!orgId) throw new Error('Organization ID not found in token');
  return orgId;
}

function membersPath(suffix = ''): string {
  return `/organizations/${requireOrgId()}/members${suffix}`;
}

// =====================
// Organization Management
// =====================

export const createOrganization = async (
  requestData: CreateOrganizationRequest,
) => {
  return makeApiCall('POST', '/organizations', requestData);
};

export const getOrganization = async (options?: { includeToken?: boolean }): Promise<{
  organization: any;
  webComponentToken?: string;
  usage?: PlatformUsageSummary;
  processorActivity?: PlatformProcessorActivity;
  achPaymentsAvailable?: boolean;
  // Guided setup checklist — present ONLY when the caller is the org owner and the
  // checklist isn't dismissed (server is the authority). Absent ⇒ never show anything.
  setupChecklist?: SetupChecklistStatus;
  /** Server-evaluated PostHog flags for this org (authoritative; do not re-evaluate client-side). */
  features?: Record<string, boolean>;
}> => {
  const queryParams = options?.includeToken ? '?includeToken=true' : '';
  try {
    const response = await makeApiCall<ItemEnvelope<{
      organization: any;
      webComponentToken?: string;
      usage?: PlatformUsageSummary;
      processorActivity?: PlatformProcessorActivity;
      achPaymentsAvailable?: boolean;
      setupChecklist?: SetupChecklistStatus;
      features?: Record<string, boolean>;
    }>>(
      'GET',
      `/organization/profile${queryParams}`,
    );
    return response.data;
  } catch (error) {
    // A valid token whose company no longer exists (deleted in a teardown or by
    // an admin) must not leave the browser "logged in" to a dead org — end the
    // session the same way an expired token does. A token without an orgId claim
    // is mid-signup (the company isn't created yet), so its 404 is expected.
    if (error instanceof ApiError && error.status === 404 && tokenHasOrgId()) {
      handleSessionExpired();
    }
    throw error;
  }
};

export const updateOrganization = async (
  requestData: UpdateOrganizationRequest,
): Promise<{ organization: any }> => {
  const response = await makeApiCall<ItemEnvelope<{ organization: any }>>(
    'PATCH',
    '/organization/profile',
    requestData,
  );
  return response.data;
};

/**
 * Save the automation doorbell (RUT-3817).
 *
 * This goes to `PUT /organization`, not the profile PATCH the rest of the
 * company tab auto-saves through, because the doorbell secret is write-only:
 * it must be sent on an explicit Save, once, and never round-tripped. Send
 * `mcpEventWebhookUrl: ''` to turn the doorbell off — the server clears the
 * stored secret along with it. Omit `mcpEventWebhookSecret` to change the URL
 * while leaving the key the bot already holds in place.
 */
export const updateOrganizationEventWebhook = async (requestData: {
  mcpEventWebhookUrl: string;
  mcpEventWebhookSecret?: string;
}): Promise<{ organization: any }> => {
  const response = await makeApiCall<ItemEnvelope<{ organization: any }>>(
    'PUT',
    '/organization',
    requestData,
  );
  return response.data;
};

export const getPlatformInvoices = async (): Promise<{ invoices: PlatformInvoice[] }> => {
  const response = await makeApiCall<ItemEnvelope<{ invoices: PlatformInvoice[] }>>(
    'GET',
    '/organization/platform-invoices',
  );
  return response.data;
};

// Create inactive organization during registration (unauthenticated)
export async function createInactiveOrganization(requestData: CreateOrganizationRequest) {
  const res = await fetch(`${getApiUrl()}/organizations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...requestData,
      isActive: false, // Explicitly set as inactive
    }),
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Organization creation failed');
  return res.json();
}

// =====================
// Organization Members Management
// =====================

/**
 * Roles in the caller's org via the org-scoped route, which anyone who can
 * invite may read. `/roles` needs roles:manage (Owner only), so an Admin's
 * invite picker came back empty from it (RUT-4122).
 */
export const getOrganizationRoles = async (): Promise<{ roles: Role[] }> => {
  const response = await makeApiCall<ListEnvelope<Role>>(
    'GET',
    `/organizations/${requireOrgId()}/roles`,
  );
  return { roles: response.data };
};

// Get organization members
export const getOrganizationMembers = async (): Promise<OrganizationMembersResponse> => {
  const response = await makeApiCall<ItemEnvelope<OrganizationMembersResponse>>(
    'GET',
    membersPath(),
  );
  return response.data;
};

// Get specific organization member
export const getOrganizationMember = async (
  memberId: string,
): Promise<{
  member: OrganizationMember;
}> => {
  const response = await makeApiCall<ItemEnvelope<{ member: OrganizationMember }>>(
    'GET',
    membersPath(`/${memberId}`),
  );
  return response.data;
};

// Invite new organization member
export const inviteOrganizationMember = async (
  memberData: InviteMemberRequest,
): Promise<{
  message: string;
  member: OrganizationMember;
  invitation: {
    inviteToken: string;
    downloadLink: string;
    expiresAt: string;
  };
}> => {
  // makeApiCall already uses makeApiCallWithRefresh internally via dynamic import
  // This ensures token has org claims before inviting members
  const response = await makeApiCall<ItemEnvelope<{
    message: string;
    member: OrganizationMember;
    invitation: {
      inviteToken: string;
      downloadLink: string;
      expiresAt: string;
    };
  }>>('POST', membersPath(), memberData);
  return response.data;
};

// Update organization member
export const updateOrganizationMember = async (
  memberId: string,
  memberData: UpdateMemberRequest,
): Promise<{
  message: string;
  member: OrganizationMember;
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    message: string;
    member: OrganizationMember;
  }>>('PATCH', membersPath(`/${memberId}`), memberData);
  return response.data;
};

// Remove organization member
export const removeOrganizationMember = async (
  memberId: string,
): Promise<{
  message: string;
  removedMemberId: string;
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    message: string;
    removedMemberId: string;
  }>>('DELETE', membersPath(`/${memberId}`));
  return response.data;
};

// Update organization member status (deactivate/reactivate)
export const updateOrganizationMemberStatus = async (
  memberId: string,
  status: 'active' | 'inactive',
): Promise<{
  message: string;
  member: OrganizationMember;
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    message: string;
    member: OrganizationMember;
  }>>('PATCH', membersPath(`/${memberId}`), { status });
  return response.data;
};

// Cancel organization member invitation
export const cancelOrganizationMemberInvitation = async (
  memberId: string,
): Promise<{
  message: string;
  member: OrganizationMember;
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    message: string;
    member: OrganizationMember;
  }>>('PATCH', membersPath(`/${memberId}`), {
    action: 'cancel_invitation',
  });
  return response.data;
};

// Resend organization member invitation
export const resendOrganizationMemberInvitation = async (
  memberId: string,
): Promise<{
  message: string;
  member: OrganizationMember;
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    message: string;
    member: OrganizationMember;
  }>>('PATCH', membersPath(`/${memberId}`), {
    action: 'resend_invitation',
  });
  return response.data;
};

// Force sign out organization member from all devices
export const signOutOrganizationMember = async (
  memberId: string,
): Promise<{
  message: string;
  member: {
    id: string;
    name: string;
  };
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    message: string;
    member: {
      id: string;
      name: string;
    };
  }>>('PATCH', membersPath(`/${memberId}`), {
    action: 'sign_out',
  });
  return response.data;
};

// Push a "turn location back on" nudge to a member's device (RUT-2638).
// Fails with error.details.reason === 'NO_PUSH_TOKEN' when the member's app
// never registered for push notifications (device unreachable).
export const sendMemberLocationNudge = async (
  memberId: string,
): Promise<{
  message: string;
  sentCount: number;
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    message: string;
    sentCount: number;
  }>>('POST', `/organizations/${requireOrgId()}/location-nudges`, { memberId });
  return response.data;
};

// Complete member onboarding with invite token
export const completeMemberOnboarding = async (
  onboardingData: {
    inviteToken: string;
    phoneNumber: string;
    /** User-provided password for web signups. Omitted when accepting with Google. */
    password?: string;
    /** Office ID token from a Google sign-in (RUT-4523); replaces the password. */
    idToken?: string;
    otpVerified: boolean;
    locationPermissionGranted: boolean;
  },
): Promise<{
  message: string;
  user: {
    id: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber: string;
    email?: string;
    role?: string;
    orgId: string;
    crewAssignments?: any[];
  };
  /** Absent when the invite was accepted with Google (no password was set). */
  credentials?: {
    username: string;
  };
}> => {
  const response = await makePublicApiCall<ItemEnvelope<{
    message: string;
    user: {
      id: string;
      name?: string;
      firstName?: string;
      lastName?: string;
      phoneNumber: string;
      email?: string;
      role?: string;
      orgId: string;
      crewAssignments?: any[];
    };
    credentials: {
      username: string;
    };
  }>>('POST', '/members/onboarding', onboardingData);
  return response.data;
};

// Migrate admin members (create missing organization member records for existing admins)
export const migrateAdminMembers = async (): Promise<{
  message: string;
  summary: {
    processed: number;
    created: number;
    skipped: number;
    errors: number;
  };
  results: Array<{
    orgId: string;
    cognitoSub: string;
    status: 'created' | 'exists' | 'error';
    error?: string;
  }>;
}> => {
  return makeApiCall('POST', '/organizations/migrate/admin-members');
};

// =====================
// Team Members (used by Chat)
// =====================

export const getTeamMembers = async (): Promise<{ members: any[] }> => {
  const response = await makeApiCall<ItemEnvelope<{ members: any[] }>>(
    'GET',
    membersPath(),
  );
  return { members: response.data.members };
};

// =====================
// Organization Asset Management (RESTful)
// =====================

export const generateLogoUploadUrl = async (
  orgId: string,
  logoData: {
    fileName: string;
    fileType: string;
  },
): Promise<{
  uploadUrl: string;
  logoUrl: string;
  message: string;
}> => {
  return makeApiCall('POST', `/organizations/${orgId}/assets/logos`, logoData);
};

export const updateOrganizationLogo = async (
  orgId: string,
  logoData: {
    logoUrl: string;
  },
): Promise<{
  message: string;
  organization: {
    id: string;
    name: string;
    logoUrl: string;
    updatedAt: string;
  };
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    message: string;
    organization: {
      id: string;
      name: string;
      logoUrl: string;
      updatedAt: string;
    };
  }>>('PATCH', `/organizations/${orgId}`, logoData);
  return response.data;
};

// Update organization settings (dispatch summary, invoice display options, charging mode, etc.)
export const updateOrganizationSettings = async (
  settings: {
    dispatchSummarySettings?: {
      enabled: boolean;
      recipientRoleIds: string[];
    };
    invoiceSettings?: {
      showLaborMinutes?: boolean;
    };
    // Global credit-card requirement lever (RUT-221). When false, no customer is
    // required to have a card on file.
    requirePaymentMethod?: boolean;
    // "Try again" on Billing when the payment partner has not registered the business.
    paymentRegistration?: 'retry';
    // When true, customers may add and be charged via ACH bank accounts.
    achPaymentsEnabled?: boolean;
    chargingMode?: ChargingMode;
    /** Minutes to wait before charging a finished visit. 0 / 15 / 30 / 60 / 120. */
    chargeWaitMinutes?: ChargeWaitMinutes;
    autoSharePhotosDefault?: boolean;
    notifyCustomerOnReviewDefault?: boolean;
    // Automated portal-login reminders lever (RUT-2973)
    portalLoginReminderEnabled?: boolean;
    hiddenRateMessageDefault?: string;
    showPortalPauseRequest?: boolean;
    // Optional crew-acknowledgment alerts to the office (RUT-2925)
    crewAckAlertSettings?: {
      urgentMessageAck?: boolean;
      noteAck?: boolean;
    };
    // First-in / last-out org clock alerts to the office (RUT-1576 / RUT-3991)
    dayBoundaryAlertSettings?: {
      startOfDay?: boolean;
      endOfDay?: boolean;
    };
    // Automatic review rules applied when a visit completes
    visitReviewSettings?: {
      crewStartSpreadEnabled?: boolean;
      crewStartSpreadMinutes?: number;
    };
    // Sales-tax filing cadence + paid marks (RUT-3183) + MN location (RUT-3922)
    taxFilingSettings?: {
      cadence: 'monthly' | 'quarterly' | 'annual';
      dueDay: number;
      state?: string;
      mnLocationNumber?: string;
      paidPeriods?: Record<string, { paidAt: string }>;
    };
    firstRun?: Pick<
      OrganizationFirstRunState,
      'hasSubcontractors' | 'hasCrews' | 'payPath' | 'firstWeekVisibleAt'
    >;
    setupChecklist?: {
      dismissed?: boolean;
      itemOverrides?: Partial<Record<SetupChecklistItemKey, boolean>>;
    };
  },
): Promise<{ organization: any }> => {
  const response = await makeApiCall<ItemEnvelope<{ organization: any }>>(
    'PATCH',
    '/organization/settings',
    settings,
  );
  return response.data;
};

// Get organization by identifier (slug or UUID)
export const getOrganizationByIdentifier = async (identifier: string): Promise<{ organization: any }> => {
  // Determine if identifier is a slug or orgId (UUID format)
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);

  if (isUuid) {
    // Use UUID-based endpoint (returns envelope from getOrgIdPublic.ts)
    const response = await makePublicApiCall<ItemEnvelope<{ organization: any }>>(
      'GET',
      `/public/organizations/${identifier}`,
    );
    return response.data;
  } else {
    // Use slug-based endpoint (still returns legacy shape directly)
    return makePublicApiCall('GET', `/public/organizations/slug/${identifier}`);
  }
};
